import { getCourses } from 'core/crud/courses.crud';
import { getGradebook } from 'core/crud/gradebook.crud';
import { useRouter } from 'core/hooks/useRouter';
import useSystemSettings from 'core/hooks/useSystemSettings';
import * as coreEntities from 'core/store/index';
import { handleApiError } from 'core/utils/apiErrorHandler';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { getCustomizedBy } from 'core/utils/course';
import { validateAndFixJSON } from 'core/utils/jsonValidator';
import { successResponse } from 'core/utils/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from './useUser';

export function useCourse(guid, updateCourse = false) {
  const dispatch = useDispatch();
  const { loadingSettings, enableEvaluationModel } = useSystemSettings();
  const { user, isStudent, schoolGuid } = useUser();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [themeColor, setThemeColor] = useState(getComputedStyle(document.documentElement).getPropertyValue('--color-first'));
  const router = useRouter();

  const courseMode = useSelector((state) => coreEntities.ui.selectors.getCourseMode(state));
  const currentCourse = useSelector((state) => coreEntities.courses.selectors.getCurrentCourse(state));
  const isSchoolProgram = useSelector((state) => coreEntities.courses.selectors.isSchoolProgram(state, guid));

  useEffect(() => {
    if (enableEvaluationModel === undefined || loadingSettings) {
      return;
    }

    if (currentCourse && currentCourse.guid === guid && !updateCourse && isStudent) {
      setLoading(true);
      setData(currentCourse);
      try {
        const json = JSON.parse(currentCourse.template.json_config);
        if (json.color) setThemeColor(json.color);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    } else {
      getCurrentCourse();
    }
  }, [guid, enableEvaluationModel, isStudent]);

  async function initStudentGradebook(course, user) {
    console.log('initStudentGradebook', course);
    const response = await getGradebook(course?.guid, getCustomizedBy(course, user), schoolGuid);
    if (successResponse(response)) {
      return response?.data?.data;
    }
  }

  async function getCurrentCourse() {
    if (!guid || loading === true) {
      return;
    } else {
      setLoading(true);
      setError(null);
      try {
        const response = await getCourses({ courseGuid: guid, schoolGuid: isSchoolProgram ? schoolGuid : null });

        if (successResponse(response)) {
          let tempCurrentCourse = {
            ...response.data.data,
            isUnattended: Boolean(response.data.data.type === COURSE_TYPES.TEMPLATE_1 || response.data.data.type === COURSE_TYPES.TEMPLATE_2),
            isCoursePersonalizedByTeacher: Boolean(
              response.data.data.type === COURSE_TYPES.TEMPLATE_2 || response.data.data.course_template_type === COURSE_TYPES.TEMPLATE_2
            ),
          };

          if (enableEvaluationModel) {
            const studentGradebook = await initStudentGradebook(tempCurrentCourse, user);
            tempCurrentCourse.studentGradebook = studentGradebook;
          }

          setData(tempCurrentCourse);
          dispatch(coreEntities.courses.actions.setCurrentCourse(tempCurrentCourse));

          try {
            const json = validateAndFixJSON(tempCurrentCourse.template.json_config);
            if (json.color) setThemeColor(json.color);
          } catch (err) {
            console.error(err);
          }
          setLoading(false);
        } else {
          const errorInfo = handleApiError(response, router, {
            user: user ? { id: user.guid, username: user.username, email: user.email || user.username, role: user.role_name } : undefined,
            context: { courseGuid: guid, action: 'getCurrentCourse' },
          });

          if (errorInfo.canRetry) {
            setError({ message: errorInfo.message, canRetry: true, type: errorInfo.type });
          }
          setLoading(false);
        }
      } catch (err) {
        // Manejar errores de red o excepciones
        const errorInfo = handleApiError(null, router, {
          user: user ? { id: user.guid, username: user.username, email: user.email || user.username, role: user.role_name } : undefined,
          context: { courseGuid: guid, action: 'getCurrentCourse', error: err.message },
        });

        if (errorInfo.canRetry) {
          setError({ message: errorInfo.message, canRetry: true, type: errorInfo.type });
        }
        setLoading(false);
      }
    }
  }

  return { data, error, loading, courseMode, themeColor, retry: getCurrentCourse };
}
